import z from 'zod';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import { MileageTrackerAddressRecordSchema } from '../mileage-tracker-address/mileageTrackerAddressTypes';
import {
  MileageTrackerPurposeSchema,
  type MileageTrackerPurposeType,
} from '../mileage-tracker-purpose/mileageTrackerPurposeTypes';
import {
  MileageTrackerVehicleSchema,
  type MileageTrackerVehicleType,
} from '../mileage-tracker-vehicle/mileageTrackerVehicleTypes';
import { MileageTrackerSchema, type MileageTrackerType } from './mileageTrackerTypes';

// refactor all other basic apis to use a standardized API interface
function createStandardizedMileageApi<T>(resourcePath: string, schema: z.ZodType<T>) {
  const RemoteSchema = schema.and(z.object({ id: z.string() }));

  const GetResponseSchema = z.object({
    resources: z.array(RemoteSchema),
  });

  const PostResponseSchema = z.object({
    resource: RemoteSchema,
    clientId: z.string().optional(),
    id: z.string(),
  });

  const PutResponseSchema = z.object({
    resource: RemoteSchema,
    id: z.string(),
  });

  return {
    async fetch(): Promise<(T & { id: string })[]> {
      const requestUrl = `${getServiceUrl()}/mileage/${resourcePath}?limit=5000`;
      return GetResponseSchema.parse((await httpClientInterface.send('GET', requestUrl)).data).resources;
    },
    async create(item: T) {
      const requestUrl = `${getServiceUrl()}/mileage/${resourcePath}`;
      return PostResponseSchema.parse((await httpClientInterface.send('POST', requestUrl, item)).data);
    },
    async update(item: T & { id: string }) {
      const requestUrl = `${getServiceUrl()}/mileage/${resourcePath}/${item.id}`;
      return PutResponseSchema.parse((await httpClientInterface.send('PUT', requestUrl, item)).data);
    },
    async delete(id: string) {
      const requestUrl = `${getServiceUrl()}/mileage/${resourcePath}/${id}`;
      await httpClientInterface.send('DELETE', requestUrl);
    },
  };
}

export const mileageTrackerTripsApi = createStandardizedMileageApi<MileageTrackerType>('trips', MileageTrackerSchema);
export const mileageTrackerPurposeApi = createStandardizedMileageApi<MileageTrackerPurposeType>(
  'purposes',
  MileageTrackerPurposeSchema,
);
export const mileageTrackerVehicleApi = createStandardizedMileageApi<MileageTrackerVehicleType>(
  'vehicles',
  MileageTrackerVehicleSchema,
);

// Stored addresses are created by the server when an address is resolved and are never changed or deleted by the app,
// so the app only reads them.
export const mileageTrackerAddressApi = {
  fetch: createStandardizedMileageApi('addresses', MileageTrackerAddressRecordSchema).fetch,
};
